import React, { useContext, useState } from "react";

import { GROUPED_BETTING_TYPES } from "@data-types/offer/event-betting-type/BettingTypeGroups";
import BettingTypeStatistics from "@features/offer/desktop/statistics/BettingTypeStatistics";
import { EventContext, OfferStoreContext } from "@gp/components";
import { EventKey, BettingType as GPBettingType } from "@gp/models";
import { getSortedEventKeysBySpecifierValue } from "@gp/offer";
import { NBATeamNameComparer, NameProvider, Sorter } from "@gp/utility";
import { BetBuilderModeContext } from "@state/context/bet-builder/BetBuilderModeContext";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";
import classnames from "classnames";
import { observer } from "mobx-react";

import { BettingTypeRow } from ".";
import BettingTypeStatisticsButton from "../../buttons/BettingTypeStatisticsButton";
import { BettingTypeToolTip } from "./BettingTypeToolTip";

const nameProvider = new NameProvider();

export const BettingType = observer(function BettingType(props: {
	bettingType: GPBettingType;
	isFavorite?: boolean;
}) {
	const { bettingType, isFavorite } = props;

	const [bettingTypeStats, setBettingTypeStats] = useState<null>(null);

	const favoritesStore = useContext(FavoritesStoreContext);
	const event = useContext(EventContext);
	const {
		eventKeysMap,
		lookupsStore: { teams },
	} = useContext(OfferStoreContext);
	const isBetBuilder = useContext(BetBuilderModeContext);

	if (eventKeysMap == null || eventKeysMap.size === 0) {
		return null;
	}

	const eventKeys = eventKeysMap.get(event.id);

	if (eventKeys == null || eventKeys.size === 0) {
		return null;
	}

	let bettingTypeKeys: EventKey[] = Array.from(eventKeys.values()).filter(
		(key) => key.bettingType.abrv === bettingType.abrv
	);

	if (isBetBuilder) {
		bettingTypeKeys = bettingTypeKeys.filter((bettingType) => {
			return bettingType.betBuilder;
		});
	}

	bettingTypeKeys = getSortedEventKeysBySpecifierValue(bettingTypeKeys);
	if (isFavorite && bettingTypeKeys[0]?.specifier) {
		bettingTypeKeys = bettingTypeKeys.filter((marginal) => {
			return favoritesStore.isUserFavoriteBettingType(
				marginal.bettingTypeId,
				marginal.specifier || null
			);
		});
	}

	const teamOneName = teams.get(event.teamOneId || "")?.name || "";

	if (
		teamOneName != null &&
		[
			"player-total-assists",
			"player-total-rebounds",
			"player-total-3-point-field-goals",
			"player-total-points",
		].includes(bettingType.abrv || "")
	) {
		bettingTypeKeys.sort(
			Sorter.sort((a, b) => NBATeamNameComparer(a, b, event, teams))
		);
	}

	return (
		<>
			{([
				"player-total-assists",
				"player-total-rebounds",
				"player-total-3-point-field-goals",
				"player-total-points",
			].includes(bettingType.abrv || "") ||
				GROUPED_BETTING_TYPES.includes(bettingType.abrv || "")) && (
				<div className="offer--additional__row offer--additional__row--header">
					<div
						className={classnames(
							"offer__body__data offer--additional__name",
							bettingTypeStats !== null
								? "widget__header--secondary"
								: ""
						)}
					>
						<div className="u-display--f u-display--f--align-center u-gap--xsml">
							{bettingType.simpleName}
							<i
								className="u-icon u-icon--sml u-icon--info-circle"
								data-tooltip-id={
									"offerRowTooltip-" +
									bettingType.simpleName.replace(" ", "")
								}
								data-tooltip-position-strategy="fixed"
							/>
							<BettingTypeToolTip
								name={bettingType.simpleName}
								description={nameProvider.getName(
									bettingType.description,
									{}
								)}
							/>
						</div>

						<BettingTypeStatisticsButton
							bettingType={props.bettingType}
							bettingTypeStats={bettingTypeStats}
							setBettingTypeStats={setBettingTypeStats}
							offerKey={bettingTypeKeys}
						/>
					</div>

					{bettingTypeStats && (
						<BettingTypeStatistics
							bettingTypeAbrv={bettingType.abrv}
							bettingTypeStats={bettingTypeStats}
						/>
					)}
				</div>
			)}

			{bettingTypeKeys.map((key, index) => {
				return (
					<BettingTypeRow
						key={key.id}
						bettingType={bettingType}
						offerKey={key}
						index={index}
						bettingTypeStats={bettingTypeStats}
						setBettingTypeStats={setBettingTypeStats}
						additionalClassName={event.isOutright ? "is-inner" : ""}
					/>
				);
			})}
		</>
	);
});
